import type { NamiPaywallEvent } from '@namiml/web-sdk';

/**
 * Print a paywall action handler event to the browser console.
 *
 * The payload is derived from the event itself rather than a per-action
 * switch, so a value added to `NamiPaywallAction` later logs correctly with
 * no change here. Keys carrying `null`/`undefined` are dropped, which leaves
 * only the fields that mean something for the action in hand — a
 * `SELECT_SKU` shows its `sku`, a `PURCHASE_FAILED` its `purchaseError`.
 *
 * Kept in sync by hand with the copy in `web-app/utils/logPaywallAction.ts`;
 * the two example apps build with different toolchains and do not share code.
 */
export function logPaywallAction(event: NamiPaywallEvent): void {
  const label = `[Nami] paywall action: ${String(event?.action ?? 'UNKNOWN')}`;
  const detail: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(event ?? {})) {
    if (key === 'action' || value === null || value === undefined) continue;
    detail[key] = value;
  }

  // Some older Tizen/webOS engines ship a console without grouping. Fall back
  // to a single line there rather than throwing inside a paywall callback.
  if (
    typeof console.groupCollapsed !== 'function' ||
    typeof console.groupEnd !== 'function'
  ) {
    console.log(label, detail);
    return;
  }

  console.groupCollapsed(label);
  console.log(detail);
  console.groupEnd();
}
